import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Hospitals.css';
import { getApiBaseUrl } from '../../services/api';
import { emitDataChange, onDataChange, DATA_TYPES, shouldRefresh } from '../../services/dataEvents';

const Hospitals = () => {
  const [hospitals, setHospitals] = useState([]);
  const [newHospital, setNewHospital] = useState({
    code: '',
    name: '',
    address: '',
    phone: '',
    capacity: 100,
    monthlySlots: 60,
    slotDuration: 24
  });
  const [editingHospital, setEditingHospital] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const apiUrl = getApiBaseUrl();

  const fetchHospitals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/api/hospitals`);
      setHospitals(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching hospitals:', error);
      setHospitals([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchHospitals();
  }, [fetchHospitals]);

  useEffect(() => {
    return onDataChange((type) => {
      if (shouldRefresh(type, [DATA_TYPES.HOSPITALS, DATA_TYPES.GROUP, DATA_TYPES.ALL])) {
        fetchHospitals();
      }
    });
  }, [fetchHospitals]);



  const handleAddHospital = async (e) => {
    e.preventDefault();
    if (!newHospital.code.trim() || !newHospital.name.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${apiUrl}/api/hospitals`, {
        code: newHospital.code,
        name: newHospital.name,
        address: newHospital.address,
        phone: newHospital.phone,
        capacity: newHospital.capacity,
        monthlySlots: newHospital.monthlySlots,
        slotDuration: newHospital.slotDuration,
      });
      setHospitals([...hospitals, response.data]);
      emitDataChange(DATA_TYPES.HOSPITALS);
      setNewHospital({
        code: '',
        name: '',
        address: '',
        phone: '',
        capacity: 100,
        monthlySlots: 60,
        slotDuration: 24
      });
    } catch (error) {
      console.error('Error adding hospital:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateHospital = async (e) => {
    e.preventDefault();
    if (!editingHospital.code.trim() || !editingHospital.name.trim()) return;

    setIsSubmitting(true);
    try {
      const hospitalId = editingHospital.id || editingHospital._id;
      const response = await axios.put(`${apiUrl}/api/hospitals/${hospitalId}`, {
        code: editingHospital.code,
        name: editingHospital.name,
        address: editingHospital.address,
        phone: editingHospital.phone,
        capacity: editingHospital.capacity,
        monthlySlots: editingHospital.monthlySlots,
        slotDuration: editingHospital.slotDuration,
      });
      setHospitals(hospitals.map(hosp => (hosp.id || hosp._id) === hospitalId ? response.data : hosp));
      setEditingHospital(null);
      emitDataChange(DATA_TYPES.HOSPITALS);
    } catch (error) {
      console.error('Error updating hospital:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteHospital = async (hospitalId) => {
    if (!window.confirm('Are you sure you want to delete this hospital?')) return;

    try {
      await axios.delete(`${apiUrl}/api/hospitals/${hospitalId}`);
      setHospitals(hospitals.filter(hosp => (hosp.id || hosp._id) !== hospitalId));
      emitDataChange(DATA_TYPES.HOSPITALS);
    } catch (error) {
      console.error('Error deleting hospital:', error);
    }
  };

  return (
    <div className="hospitals-container">
      <div className="hospitals-header">
        <h2>Hospital Management</h2>
        <p>Manage hospital information and locations</p>
      </div>

      <div className="hospitals-content">
        {/* Add New Hospital Form */}
        <div className="add-hospital-section">
          <h3>Add New Hospital</h3>
          <form onSubmit={handleAddHospital} className="hospital-form">
            <div className="form-row">
              <div className="form-group">
                <label>Hospital Code</label>
                <input
                  type="text"
                  value={newHospital.code}
                  onChange={(e) => setNewHospital({ ...newHospital, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., MPH"
                  maxLength="5"
                  required
                />
              </div>
              <div className="form-group">
                <label>Hospital Name</label>
                <input
                  type="text"
                  value={newHospital.name}
                  onChange={(e) => setNewHospital({ ...newHospital, name: e.target.value })}
                  placeholder="e.g., Morton Plant Hospital"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={newHospital.address}
                  onChange={(e) => setNewHospital({ ...newHospital, address: e.target.value })}
                  placeholder="Full hospital address"
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={newHospital.phone}
                  onChange={(e) => setNewHospital({ ...newHospital, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Bed Capacity</label>
                <input
                  type="number"
                  min="1"
                  max="2000"
                  value={newHospital.capacity}
                  onChange={(e) => setNewHospital({ ...newHospital, capacity: parseInt(e.target.value) })}
                  placeholder="Number of beds"
                />
              </div>
              <div className="form-group">
                <label>Monthly Slots</label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={newHospital.monthlySlots}
                  onChange={(e) => setNewHospital({ ...newHospital, monthlySlots: parseInt(e.target.value) })}
                  placeholder="Slots per month"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Slot Duration (Hours)</label>
              <select
                value={newHospital.slotDuration}
                onChange={(e) => setNewHospital({ ...newHospital, slotDuration: parseInt(e.target.value) })}
              >
                <option value={8}>8 Hours</option>
                <option value={12}>12 Hours</option>
                <option value={24}>24 Hours</option>
              </select>
            </div>

            <button type="submit" disabled={isSubmitting} className="submit-btn">
              {isSubmitting ? 'Adding...' : 'Add Hospital'}
            </button>
          </form>
        </div>

        {/* Hospitals List */}
        <div className="hospitals-list-section">
          <h3>Registered Hospitals ({hospitals.length})</h3>
          {loading ? (
            <div className="loading">Loading hospitals...</div>
          ) : (
            <div className="hospitals-grid">
              {hospitals.map(hospital => (
                <div key={hospital.id || hospital._id} className="hospital-card">
                  <div className="hospital-header">
                    <div className="hospital-code">{hospital.code}</div>
                    <div className="hospital-actions">
                      <button
                        onClick={() => setEditingHospital(hospital)}
                        className="edit-btn"
                        title="Edit Hospital"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteHospital(hospital.id || hospital._id)}
                        className="delete-btn"
                        title="Delete Hospital"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div className="hospital-details">
                    <h4 className="hospital-name">{hospital.name}</h4>
                    {hospital.address && (
                      <p className="hospital-address">
                        <strong>Address:</strong> {hospital.address}
                      </p>
                    )}
                    {hospital.phone && (
                      <p className="hospital-phone">
                        <strong>Phone:</strong> {hospital.phone}
                      </p>
                    )}
                    <p className="hospital-capacity">
                      <strong>Capacity:</strong> {hospital.capacity} beds
                    </p>
                    <p className="hospital-slots">
                      <strong>Monthly Slots:</strong> {hospital.monthlySlots || 60} slots
                    </p>
                    <p className="hospital-duration">
                      <strong>Slot Duration:</strong> {hospital.slotDuration || 24} hours
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit Hospital Modal */}
        {editingHospital && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Edit Hospital</h3>
              <form onSubmit={handleUpdateHospital} className="hospital-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Hospital Code</label>
                    <input
                      type="text"
                      value={editingHospital.code}
                      onChange={(e) => setEditingHospital({ ...editingHospital, code: e.target.value.toUpperCase() })}
                      maxLength="5"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Hospital Name</label>
                    <input
                      type="text"
                      value={editingHospital.name}
                      onChange={(e) => setEditingHospital({ ...editingHospital, name: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Address</label>
                    <input
                      type="text"
                      value={editingHospital.address}
                      onChange={(e) => setEditingHospital({ ...editingHospital, address: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={editingHospital.phone}
                      onChange={(e) => setEditingHospital({ ...editingHospital, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Bed Capacity</label>
                    <input
                      type="number"
                      min="1"
                      max="2000"
                      value={editingHospital.capacity}
                      onChange={(e) => setEditingHospital({ ...editingHospital, capacity: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Monthly Slots</label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={editingHospital.monthlySlots}
                      onChange={(e) => setEditingHospital({ ...editingHospital, monthlySlots: parseInt(e.target.value) })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Slot Duration (Hours)</label>
                  <select
                    value={editingHospital.slotDuration}
                    onChange={(e) => setEditingHospital({ ...editingHospital, slotDuration: parseInt(e.target.value) })}
                  >
                    <option value={8}>8 Hours</option>
                    <option value={12}>12 Hours</option>
                    <option value={24}>24 Hours</option>
                  </select>
                </div>

                <div className="modal-actions">
                  <button type="submit" disabled={isSubmitting} className="submit-btn">
                    {isSubmitting ? 'Updating...' : 'Update Hospital'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingHospital(null)}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Hospitals;
